import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { useAuth } from "../hooks/use-auth";
import { useProfile } from "../hooks/use-profile";

interface ProtectedRouteProps {
  /**
   * Shown to a signed-in account that has no profile. Only SSO sign-ins become
   * members, so this is someone who reached the app some other way.
   */
  noAccess: ReactNode;
  /** Shown while the session and profile load, e.g. a skeleton of the app shell. */
  loading?: ReactNode;
}

export function ProtectedRoute({ noAccess, loading = null }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const { profile, isLoading: isProfileLoading, error } = useProfile();
  const location = useLocation();

  if (isLoading) return <>{loading}</>;

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (isProfileLoading) return <>{loading}</>;
  // A failed lookup is a backend problem, not a verdict on membership; let the
  // route render and surface its own errors rather than claiming no access.
  if (!profile && !error) return <>{noAccess}</>;

  return <Outlet />;
}
